import { Box, xcss } from '@atlaskit/primitives';
import React, { useMemo, useState } from 'react';

// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import styled from '@emotion/styled';
// eslint-disable-next-line @atlaskit/ui-styling-standard/use-compiled -- Ignored via go/DSP-18766
import { keyframes } from '@emotion/react';
import { useIntl } from 'react-intl-next';

import Button from '@atlaskit/button/new';
import { ConfluenceLogo } from '@atlaskit/logo';
import { token } from '@atlaskit/tokens';
import { ExperienceStart } from '@atlassian/experience-tracker';
import { LoginToConfluenceIcon } from '../assets/LoginPageIconSVG';
import { ExperienceNames } from '../experience-tracker/ExperienceNames';
import { type useUrl } from './useUrl';

import RefreshBanner from './RefreshBanner';
import messages from './messages';

export type LoginPageProps = Pick<Parameters<typeof useUrl>[0], 'contentId' | 'spaceKey'> & {
	reloadEP: () => void;
	url?: string | null;
};

const SUCCESS_URL_SUFFIX = '/wiki/embed/login-confirmation';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const StyledParentDiv = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'': {
		display: 'inline-grid',
		alignItems: 'center',
		justifyItems: 'center',
		marginTop: token('space.1000', '80px'),
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const StyledConfluenceIcon = styled(LoginToConfluenceIcon)({
	marginTop: token('space.250', '20px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const StyledConfluenceLoginText = styled.h2({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	'': {
		marginTop: token('space.250', '20px'),
	},
});

const buttonWrapperStyles = xcss({ marginTop: 'space.400' });

const fadeIn = keyframes({
	from: {
		opacity: 0,
	},
	to: {
		opacity: 1,
	},
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const RefreshBannerFade = styled.div({
	padding: token('space.250', '20px'),
	animation: `${fadeIn} 3s`,
});

export const LoginPage = ({ contentId, reloadEP, spaceKey, url }: LoginPageProps) => {
	const { locale, formatMessage } = useIntl();

	const loginUrl = useMemo(() => {
		const origin = url ? new URL(url).origin : window?.location?.origin;
		return `${origin}/login?application=confluence&dest-url=${encodeURIComponent(
			`${SUCCESS_URL_SUFFIX}?locale=${locale}`,
		)}`;
	}, [url, locale]);

	const handleClick = () => {
		openLoginPopup(loginUrl);
		setDisplayRefreshBanner(!displayRefreshBanner);
	};

	const [displayRefreshBanner, setDisplayRefreshBanner] = useState(false);

	return (
		<StyledParentDiv>
			<ExperienceStart name={ExperienceNames.LOGIN_PAGE} id={`${spaceKey}-${contentId}`} />
			<ConfluenceLogo size="large" appearance="brand" />
			<StyledConfluenceIcon />
			<StyledConfluenceLoginText>{formatMessage(messages.loginText)}</StyledConfluenceLoginText>
			<Box xcss={buttonWrapperStyles}>
				<Button
					testId="login-button"
					aria-label={formatMessage(messages.loginAriaLabel)}
					appearance="primary"
					onClick={handleClick}
				>
					{formatMessage(messages.loginButton)}
				</Button>
			</Box>
			{displayRefreshBanner && (
				<RefreshBannerFade>
					<RefreshBanner
						reloadEP={reloadEP}
						title={formatMessage(messages.refreshBanner)}
						actionLabel={formatMessage(messages.refreshBannerLink)}
					/>
				</RefreshBannerFade>
			)}
		</StyledParentDiv>
	);
};

function openLoginPopup(loginUrl: string) {
	const windowFeatures = 'popup=yes,left=100,top=100,width=640,height=640';
	window.open(loginUrl, '_blank', windowFeatures);
}
